import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import BookmarkAddedRoundedIcon from '@mui/icons-material/BookmarkAddedRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import type { StudioPreferenceSlot, StudioPreferencesStore } from '../../../constants';
import { STUDIO_FORMATS, STUDIO_PREFERENCE_SLOT_COUNT } from '../../../constants';
import { getDefaultSlotName } from '../../../utils/studioPreferences';

interface PreferencesControlProps {
  intl: any;
  preferencesStore: StudioPreferencesStore;
  selectedSlot: number;
  slotName: string;
  onSelectSlot: (slotIndex: number) => void;
  onChangeSlotName: (name: string) => void;
  onRenameSlot: () => void;
  onSave: () => void;
  onClear: () => void;
}

const getSlotLabel = (intl: any, slot: StudioPreferenceSlot, slotIndex: number): string => {
  if (!slot) {
    return intl.formatMessage({ id: 'hot-topic-studio-layout-slot-empty' });
  }
  const trimmedName = slot.name?.trim();
  if (trimmedName) {
    return trimmedName;
  }
  return getDefaultSlotName(slotIndex, intl.formatMessage);
};

const SlotPreview: React.FC<{ slot: StudioPreferenceSlot }> = ({ slot }) => {
  if (!slot) {
    return (
      <span className="studio-preferences-slot-preview is-empty" aria-hidden="true">
        <AddRoundedIcon />
      </span>
    );
  }

  const format = STUDIO_FORMATS[slot.format as keyof typeof STUDIO_FORMATS] || STUDIO_FORMATS.post;
  const themeColor = slot.themeColor || '#3966f8';

  return (
    <span
      className="studio-preferences-slot-preview is-filled"
      aria-hidden="true"
      style={{ '--slot-theme': themeColor } as React.CSSProperties}
    >
      <span className="studio-preferences-slot-swatch" />
      <span className="studio-preferences-slot-meta">
        <span className="studio-preferences-slot-ratio">{format.ratio}</span>
      </span>
    </span>
  );
};

const PreferencesControl: React.FC<PreferencesControlProps> = ({
  intl,
  preferencesStore,
  selectedSlot,
  slotName,
  onSelectSlot,
  onChangeSlotName,
  onRenameSlot,
  onSave,
  onClear
}) => {
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const selectedSlotFilled = Boolean(preferencesStore.slots[selectedSlot]);
  const selectedLabel = getSlotLabel(intl, preferencesStore.slots[selectedSlot], selectedSlot);

  const handleConfirmClear = () => {
    setConfirmClearOpen(false);
    onClear();
  };

  return (
    <div className="studio-preferences-control">
      <div
        className="studio-preferences-slots"
        role="radiogroup"
        aria-label={intl.formatMessage({ id: 'hot-topic-studio-defaults' })}
      >
        {Array.from({ length: STUDIO_PREFERENCE_SLOT_COUNT }, (_, slotIndex) => {
          const slot = preferencesStore.slots[slotIndex];
          const filled = Boolean(slot);
          const selected = selectedSlot === slotIndex;
          const label = getSlotLabel(intl, slot, slotIndex);

          return (
            <button
              key={slotIndex}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={label}
              className={`studio-preferences-slot${selected ? ' selected' : ''}${filled ? ' is-filled' : ' is-empty'}`}
              onClick={() => onSelectSlot(slotIndex)}
            >
              <SlotPreview slot={slot} />
              <span className="studio-preferences-slot-name">{label}</span>
              {selected && filled ? <CheckRoundedIcon className="studio-preferences-slot-check" /> : null}
            </button>
          );
        })}
      </div>

      <div className="studio-preferences-editor">
        <TextField
          {...({
            fullWidth: true,
            size: 'small',
            variant: 'outlined',
            className: 'studio-preferences-name-field',
            label: intl.formatMessage({ id: 'hot-topic-studio-layout-name' }),
            placeholder: getDefaultSlotName(selectedSlot, intl.formatMessage),
            value: slotName,
            inputProps: { maxLength: 24 },
            InputLabelProps: { shrink: true },
            onChange: (event: React.ChangeEvent<HTMLInputElement>) => onChangeSlotName(event.target.value),
            onBlur: onRenameSlot,
            onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter') {
                (event.target as HTMLInputElement).blur();
              }
            }
          } as any)}
        />

        <div className="studio-preferences-actions">
          <Button
            variant="contained"
            color="primary"
            className="studio-preferences-save"
            startIcon={<BookmarkAddedRoundedIcon />}
            onClick={onSave}
          >
            {intl.formatMessage({ id: 'hot-topic-studio-save-layout' })}
          </Button>
          {selectedSlotFilled ? (
            <Tooltip title={intl.formatMessage({ id: 'hot-topic-studio-reset-layout' })} arrow>
              <IconButton
                className="studio-preferences-clear"
                color="primary"
                aria-label={intl.formatMessage({ id: 'hot-topic-studio-reset-layout' })}
                onClick={() => setConfirmClearOpen(true)}
              >
                <DeleteOutlineRoundedIcon />
              </IconButton>
            </Tooltip>
          ) : null}
        </div>
      </div>

      <Dialog
        open={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        {...({
          PaperProps: { className: 'studio-web-share-dialog' },
          BackdropProps: { className: 'studio-web-share-backdrop' }
        } as any)}
      >
        <DialogTitle>{intl.formatMessage({ id: 'hot-topic-studio-delete-layout-title' })}</DialogTitle>
        <DialogContent>
          <Typography>
            {intl.formatMessage({ id: 'hot-topic-studio-delete-layout-description' }, { name: selectedLabel })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button className="studio-web-share-cancel" onClick={() => setConfirmClearOpen(false)}>
            {intl.formatMessage({ id: 'Cancel' })}
          </Button>
          <Button className="studio-web-share-confirm" variant="contained" color="primary" onClick={handleConfirmClear}>
            {intl.formatMessage({ id: 'Delete' })}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PreferencesControl;
